import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { useBasket } from '@/lib/cart';
import CourseCard from '@/components/CourseCard';
import CourseModal from '@/components/CourseModal';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../../server/src/routers/index';

type Course = inferRouterOutputs<AppRouter>['courses']['list'][number];

export default function CoursesPage() {
  const { user, signIn } = useAuth();
  const { addItem } = useBasket();
  const nav = useNavigate();
  const [selected, setSelected] = useState<Course | null>(null);

  const { data: courses = [], isLoading } = trpc.courses.list.useQuery();
  const { data: categories = [] }  = trpc.categories.list.useQuery();
  const { data: myPurchases = [] } = trpc.purchases.getUserPurchases.useQuery(
    undefined, { enabled: !!user }
  );

  const purchasedIds = new Set(myPurchases.map(p => p.courseId));

  // A course can belong to several majors (via the join relation); fall back to
  // the legacy single categoryId for older data.
  const majorIdsOf = (c: any): number[] => {
    const fromJoin = (c.courseCategories ?? []).map((cc: any) => cc.categoryId);
    if (fromJoin.length) return fromJoin;
    return c.categoryId != null ? [c.categoryId] : [];
  };

  // View ('all' vs my purchased) + major filter (null = All, 'other' = no major).
  type MajorFilter = number | 'other' | null;
  const [view, setView]               = useState<'all' | 'mine'>('all');
  const [majorFilter, setMajorFilter] = useState<MajorFilter>(null);
  const hasOther = courses.some(c => majorIdsOf(c).length === 0);

  const base = view === 'mine' ? courses.filter(c => purchasedIds.has(c.id)) : courses;
  const visibleCourses = base.filter(c => {
    if (majorFilter == null) return true;
    if (majorFilter === 'other') return majorIdsOf(c).length === 0;
    return majorIdsOf(c).includes(majorFilter);
  });

  const initiatePayment = trpc.purchases.initiatePayment.useMutation({
    onSuccess: (res) => { window.location.href = res.paymentUrl; },
  });

  /**
   * One-click enrol — must be logged in. Adds the course to the basket and
   * kicks off the payment portal flow for just this course.
   */
  const handlePurchase = (id: number) => {
    if (!user) { signIn('/courses'); return; }

    const course = courses.find(c => c.id === id);
    if (course) {
      addItem({ id: course.id, title: course.title, price: parseFloat(course.price as any) });
    }
    initiatePayment.mutate({ courseIds: [id] });
  };

  // Suppress unused-variable warning while keeping nav for future use
  void nav;

  if (isLoading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent
                      rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-black py-16 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">

        <div className="mb-10">
          <span className="mp-pill">Courses</span>
          <h1 className="text-4xl font-bold text-white mt-4 mb-2 tracking-tight">
            Browse Courses
          </h1>
          <p className="text-[#E4E4E7] text-lg">
            Structured video courses to master engineering fundamentals.
          </p>
        </div>

        {/* All / My Courses toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setView('all')}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border
                        ${view === 'all'
                          ? 'bg-yellow-400 text-black border-yellow-400'
                          : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
          >
            All Courses
          </button>
          <button
            onClick={() => { if (!user) { signIn('/courses'); return; } setView('mine'); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border
                        ${view === 'mine'
                          ? 'bg-yellow-400 text-black border-yellow-400'
                          : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
          >
            My Courses
          </button>
        </div>

        {/* Major filter — dropdown on mobile, chips on desktop */}
        {(categories.length > 0 || hasOther) && (() => {
          const valueOf = (f: MajorFilter) => f == null ? 'all' : String(f);
          const parse = (v: string): MajorFilter =>
            v === 'all' ? null : v === 'other' ? 'other' : Number(v);

          return (
            <div className="mb-8">
              {/* Mobile: a single, tidy dropdown */}
              <div className="sm:hidden">
                <label className="block text-xs font-semibold text-[#A1A1AA] mb-2 uppercase tracking-wide">
                  Filter by major
                </label>
                <div className="relative">
                  <select
                    value={valueOf(majorFilter)}
                    onChange={e => setMajorFilter(parse(e.target.value))}
                    className="w-full appearance-none bg-[#18181B] text-white font-semibold text-sm
                               border border-[#27272A] rounded-xl pl-4 pr-10 py-3
                               focus:outline-none focus:border-yellow-400/60"
                  >
                    <option value="all">All majors</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                    {hasOther && <option value="other">Other</option>}
                  </select>
                  <svg className="w-4 h-4 text-[#A1A1AA] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                       fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Desktop: chip row */}
              <div className="hidden sm:flex flex-wrap gap-2">
                <button
                  onClick={() => setMajorFilter(null)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border
                              ${majorFilter == null
                                ? 'bg-yellow-400 text-black border-yellow-400'
                                : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
                >
                  All
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setMajorFilter(cat.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border
                                ${majorFilter === cat.id
                                  ? 'bg-yellow-400 text-black border-yellow-400'
                                  : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
                  >
                    {cat.name}
                  </button>
                ))}
                {hasOther && (
                  <button
                    onClick={() => setMajorFilter('other')}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border
                                ${majorFilter === 'other'
                                  ? 'bg-yellow-400 text-black border-yellow-400'
                                  : 'bg-[#18181B] text-[#E4E4E7] border-[#27272A] hover:border-yellow-400/40'}`}
                  >
                    Other
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {courses.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">📚</p>
            <p className="text-[#52525B] text-lg">No courses available yet.</p>
          </div>
        ) : visibleCourses.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#52525B] text-lg">
              {view === 'mine'
                ? "You haven't purchased any courses yet."
                : 'No courses in this major yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCourses.map(course => (
              <CourseCard
                key={course.id}
                course={course}
                purchased={purchasedIds.has(course.id)}
                onPurchase={handlePurchase}
                onClick={setSelected}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <CourseModal
          course={selected}
          purchased={purchasedIds.has(selected.id)}
          onPurchase={handlePurchase}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
